/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { KahuContext } from '@/context';
import * as conversationService from '@/services/kahuConversationService';
import { streamKahuMessages } from '@/services/kahuEventStreamService';
import {
  ChatMessage,
  Conversation,
  KahuEvent,
  KahuRequestBody,
  KahuView,
} from 'types/kahu';

import { useApplications } from '@/hooks/useApplications';

import { useLanguage } from './LanguageProvider';

type KahuProviderProps = {
  children: ReactNode;
};

export const KahuProvider = ({ children }: KahuProviderProps) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [sendingSessionId, setSendingSessionId] = useState<string | null>(null);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isSwitchingConversation, setIsSwitchingConversation] = useState(false);
  const [currentView, setCurrentView] = useState<KahuView>('chat');
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [draftPrompt, setDraftPrompt] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const requestStartRef = useRef<number | null>(null);
  const pendingContextRef = useRef<string>('');
  const pendingAssistantIdRef = useRef<string | null>(null);
  const pendingTurnIdRef = useRef<string | null>(null);
  // Resolves with the session id of the in-flight request: immediately for an existing chat, or on RUN_STARTED for a new one. Lets stop wait for the id.
  const sessionIdWaiterRef = useRef<{
    promise: Promise<string>;
    resolve: (id: string) => void;
  } | null>(null);

  const cancelRequestedRef = useRef(false);

  const { messages: languageMessages } = useLanguage();
  const { selectedApps, apps, filteredAppNames } = useApplications();
  const pathname = usePathname();

  const isAppsListingPath = /\/apps$/.test(pathname);
  const appName = pathname.match(/\/apps\/([^/?#]+)/)?.[1];
  // On a workflow (component) page the slug after /components/ is the workflow
  // name. The /components listing route has no slug, so this stays undefined.
  const workflowName = pathname.match(/\/components\/([^/?#]+)/)?.[1];

  const hasMultipleConversations = conversations.length > 0;

  // refs to avoid stale state
  const currentViewRef = useRef(currentView);
  const activeConversationIdRef = useRef(activeConversationId);

  useEffect(() => {
    currentViewRef.current = currentView;
  }, [currentView]);

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  // Initial load
  useEffect(() => {
    conversationService.getAllConversations().then(setConversations);
  }, []);

  // Refresh on tab focus
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState === 'visible') {
        const updated = await conversationService.getAllConversations();
        setConversations(updated);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // Attach unread count to conversations
  const conversationsWithUnreadCounts = conversations.map((conv) => ({
    ...conv,
    unreadCount: unreadCounts[conv.id] || 0,
  }));

  /**
   * Interim tool-call messages arrive with the raw app id as the last word
   * (e.g. "Fetching error traces for my-app_4d7ybf"). Swap that id for the
   * app's display name so the user sees something readable. If the last
   * word doesn't match any known app, the message is returned untouched.
   */
  const formatInterimMessage = (message: string): string => {
    const parts = message.split(' ');
    const lastWord = parts[parts.length - 1];
    const matchedApp = apps.find((app) => app.name === lastWord);
    if (!matchedApp) return message;
    return [...parts.slice(0, -1), matchedApp.display_name].join(' ');
  };

  /**
   * Prepares the request body for the Kahu API based on the user query and context.
   * @param query - user query for kahu
   * @param context - applications names
   * @returns KahuRequestBody - request body to send to Kahu API
   */
  const prepareKahuRequestBody = (query: string) => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const app_names = appName
      ? [appName]
      : isAppsListingPath && selectedApps.length > 0
        ? filteredAppNames
        : undefined;

    const userContext: {
      timezone: string;
      app_names?: string[];
      workflow_name?: string;
    } = {
      timezone,
      ...(app_names && { app_names }),
      ...(workflowName && { workflow_name: workflowName }),
    };

    const body: KahuRequestBody = { query, user_context: userContext };

    if (sessionId) {
      body.session_id = sessionId;
    }

    return body;
  };

  /**
   * Sends a message to the Kahu chat agent, handling the conversation flow,
   * updating messages, managing loading state, and tracking unread messages.
   * @param query user query for kahu chat agent
   */
  const sendMessage = async (query: string) => {
    if (!query.trim()) return;

    // Abort any ongoing request before starting a new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const abortSignal = abortControllerRef.current.signal;
    cancelRequestedRef.current = false;

    let resolveSessionId!: (id: string) => void;

    const sessionIdPromise = new Promise<string>((resolve) => {
      resolveSessionId = resolve;
    });
    sessionIdWaiterRef.current = {
      promise: sessionIdPromise,
      resolve: resolveSessionId,
    };

    if (sessionId) resolveSessionId(sessionId);

    // Create a user message for chat history
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      response: query,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsSending(true);
    setSendingSessionId(activeConversationId);

    // Track when the request for query started to calculate duration later
    const requestStart = Date.now();
    requestStartRef.current = requestStart;

    try {
      const assistantId = crypto.randomUUID();
      let assistantResponse = '';
      let conversationId: string | null = null;
      let textMessageEnded = false;
      let errorOccurred = false;

      setMessages((prev) => [
        ...prev,
        {
          id: assistantId,
          role: 'assistant',
          response: '',
          timestamp: Date.now(),
          status: 'thinking',
        },
      ]);
      pendingAssistantIdRef.current = assistantId;

      const onEvent = (event: KahuEvent) => {
        // After stop, the stream is only kept open to receive the session id.
        if (cancelRequestedRef.current && event.type !== 'RUN_STARTED') return;

        switch (event.type) {
          case 'RUN_STARTED': {
            // The session id arrives in the first event; store it right away so the stop button can hit the stop API on the first turn too.
            if (event.sessionId) {
              conversationId = event.sessionId;
              resolveSessionId(event.sessionId);

              if (!sessionId && !cancelRequestedRef.current) {
                setSessionId(event.sessionId);
                setActiveConversationId(event.sessionId);
                setSendingSessionId(event.sessionId);
              }
            }
            break;
          }
          case 'TOOL_CALL_START': {
            // Surface the latest tool-call description in the thinking bubble.
            const formatted = formatInterimMessage(event.message);
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantId
                  ? { ...msg, interimMessage: formatted }
                  : msg
              )
            );
            break;
          }
          case 'TEXT_MESSAGE_CONTENT': {
            assistantResponse += event.delta;
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantId
                  ? {
                      ...msg,
                      response: assistantResponse,
                      // Hide the interim UI as soon as the real response starts.
                      interimMessage: undefined,
                    }
                  : msg
              )
            );
            break;
          }
          case 'TEXT_MESSAGE_END': {
            textMessageEnded = true;
            break;
          }
          case 'FOLLOW_UP_QUESTIONS': {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantId
                  ? { ...msg, followUpQuestions: event.questions }
                  : msg
              )
            );
            break;
          }
          case 'STATE_SNAPSHOT': {
            conversationId = event.snapshot.sessionId;
            pendingTurnIdRef.current = event.snapshot.turnId;

            if (!sessionId && conversationId) {
              setSessionId(conversationId);
              setActiveConversationId(conversationId);
              setSendingSessionId(conversationId);
            }
            // Surface the running token count on the assistant message.
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantId
                  ? { ...msg, tokenCount: event.snapshot.tokenCount }
                  : msg
              )
            );
            break;
          }
          case 'RUN_FINISHED': {
            const turnId = pendingTurnIdRef.current;

            if (turnId) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantId
                    ? {
                        ...msg,
                        turnId,
                      }
                    : msg
                )
              );
            }

            break;
          }

          case 'RUN_ERROR': {
            // If the text response already finished streaming, the error is
            // background processing (e.g. post-run side effects) and can be
            // safely ignored. Otherwise surface it via KahuErrorResponse by
            // morphing the placeholder into a role:'error' message.
            if (textMessageEnded) break;
            errorOccurred = true;
            const errorText =
              event.message ||
              languageMessages?.KahuAI?.default_error_message ||
              '';
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantId
                  ? {
                      ...msg,
                      role: 'error',
                      response: errorText,
                      status: 'error',
                    }
                  : msg
              )
            );
            break;
          }
          default:
            break;
        }
      };

      const body = prepareKahuRequestBody(query);
      await streamKahuMessages(body, onEvent, abortSignal);

      // Stop already updated the UI and state.
      if (cancelRequestedRef.current) return;

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId
            ? {
                ...msg,
                status: errorOccurred ? msg.status : 'completed',
                timestamp: Date.now(),
                duration: parseFloat(
                  ((Date.now() - requestStart) / 1000).toFixed(1)
                ),
              }
            : msg
        )
      );
      setIsSending(false);
      setSendingSessionId(null);
      pendingContextRef.current = '';
      requestStartRef.current = null;
      pendingAssistantIdRef.current = null;
      pendingTurnIdRef.current = null;

      // UNREAD LOGIC — skip on error since no real response was delivered.
      if (!errorOccurred && conversationId) {
        const isUserViewingThisConversation =
          currentViewRef.current === 'chat' &&
          activeConversationIdRef.current === conversationId;

        if (!isUserViewingThisConversation) {
          setUnreadCounts((prev) => ({
            ...prev,
            [conversationId as string]:
              (prev[conversationId as string] || 0) + 1,
          }));
        }
      }

      // Refresh conversations
      const updated = await conversationService.getAllConversations();
      setConversations(updated);
    } catch (error: any) {
      if (error.name !== 'AbortError' && !cancelRequestedRef.current) {
        const defaultMessage = languageMessages?.KahuAI?.default_error_message;

        const apiMessage =
          typeof error?.data?.error?.message === 'string'
            ? error.data.error.message
            : typeof error?.message === 'string'
              ? error.message
              : '';

        const errorDuration = parseFloat(
          ((Date.now() - requestStart) / 1000).toFixed(1)
        );
        const placeholderId = pendingAssistantIdRef.current;

        if (placeholderId) {
          // Morph the streaming placeholder into an error message,
          // preserving any partial text that already streamed in.
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === placeholderId
                ? {
                    ...msg,
                    role: 'error',
                    response: msg.response || defaultMessage || apiMessage,
                    duration: errorDuration,
                    status: 'error',
                  }
                : msg
            )
          );
        } else {
          setMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              role: 'error',
              response: defaultMessage || apiMessage,
              timestamp: Date.now(),
              duration: errorDuration,
              status: 'error',
            },
          ]);
        }

        setIsSending(false);
        setSendingSessionId(null);
        pendingContextRef.current = '';
        requestStartRef.current = null;
        pendingAssistantIdRef.current = null;
      }
    }
  };

  /**
   * Switches to a different conversation by loading its messages, updating the active conversation and session IDs, and navigating to the chat view.
   * It also resets the unread message count for the selected conversation.
   * @param id The ID of the conversation to switch to.
   */
  const switchConversation = useCallback(async (id: string) => {
    setUnreadCounts((prev) => ({ ...prev, [id]: 0 }));
    setIsSwitchingConversation(true);
    setCurrentView('chat');
    try {
      const messages = await conversationService.getSessionById(id);

      setActiveConversationId(id);
      setSessionId(id);
      setMessages(messages);
    } finally {
      setIsSwitchingConversation(false);
    }
  }, []);

  /**
   * Create a new conversation by resetting the active conversation ID, session ID, messages, and navigating to the chat view.
   * This allows the user to start fresh without any previous context or messages.
   */
  const createNewConversation = useCallback(() => {
    onCancelRequest();
    setActiveConversationId(null);
    setSessionId(null);
    setMessages([]);
    setCurrentView('chat');
  }, []);

  /**
   * Navigates the user to the conversation list view, resets the active conversation and session,
   */
  const goToConversationList = useCallback(async () => {
    setCurrentView('conversation-list');
    setActiveConversationId(null);
    setIsLoadingConversations(true);
    try {
      const all = await conversationService.getAllConversations();
      setConversations(all);
    } finally {
      setIsLoadingConversations(false);
    }
  }, []);

  /**
   * Filters conversations based on a search query matching the conversation title.
   * If the query is empty, it returns all conversations.
   * The search is case-insensitive.
   * @param query The search string to filter conversations by title.
   * @returns An array of conversations that match the search query.
   */
  const searchConversation = useCallback(
    (query: string): Conversation[] => {
      if (!query.trim()) return conversations;
      const lower = query.toLowerCase();
      return conversations.filter((conv) =>
        conv.title.toLowerCase().includes(lower)
      );
    },
    [conversations]
  );

  /**
   * Cancels the ongoing message request to the Kahu chat agent, updates the UI accordingly,
   * and adds a cancelled message to the chat history with the duration of the request before cancellation.
   */
  const onCancelRequest = async () => {
    if (!abortControllerRef.current) return;

    const abortController = abortControllerRef.current;
    const sessionIdWaiter = sessionIdWaiterRef.current;
    const currentSessionId = sessionId;
    const requestStart = requestStartRef.current;
    const cancelDuration = requestStart
      ? parseFloat(((Date.now() - requestStart) / 1000).toFixed(1))
      : 0;

    cancelRequestedRef.current = true;
    abortControllerRef.current = null;
    sessionIdWaiterRef.current = null;
    requestStartRef.current = null;
    setSendingSessionId(null);
    setIsSending(false);

    // Update UI synchronously, before waiting on the backend.
    const placeholderId = pendingAssistantIdRef.current;

    if (placeholderId) {
      // Morph the streaming placeholder into a cancelled message,
      // preserving any partial text that already streamed in.
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === placeholderId
            ? { ...msg, duration: cancelDuration, status: 'cancelled' }
            : msg
        )
      );
    } else {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          response: '',
          timestamp: Date.now(),
          duration: cancelDuration,
          status: 'cancelled',
        },
      ]);
    }

    pendingContextRef.current = '';
    pendingAssistantIdRef.current = null;

    // Notify the backend, but don't let its failure affect local state.
    // If stop was clicked before RUN_STARTED, the id isn't known yet. Keep the stream open (UI already shows cancelled) until the id arrives, then stop the turn and only then drop the connection.
    try {
      const idToStop =
        currentSessionId ??
        (await Promise.race([
          sessionIdWaiter?.promise ?? Promise.resolve(null),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000)),
        ]));

      if (idToStop) await conversationService.cancelSessionTurn(idToStop);
    } catch (err) {
      console.error('Failed to cancel session turn on backend:', err);
    } finally {
      abortController.abort();
    }
  };

  /**
   * Resets the current chat session by clearing the session ID, messages, and aborting any ongoing requests.
   */
  const onResetSession = async () => {
    await onCancelRequest();
    setSessionId(null);
    setMessages([]);
  };

  // Submit feedback for a specific turn in the conversation. Applies the rating
  // optimistically, then reverts it if the backend call fails.
  const submitFeedback = async (
    turnId: string,
    rating: 'good' | 'bad',
    comment?: string
  ) => {
    if (!sessionId) return;

    const previousFeedback = messages.find(
      (message) => message.turnId === turnId
    )?.feedback;

    setMessages((prev) =>
      prev.map((message) =>
        message.turnId === turnId ? { ...message, feedback: rating } : message
      )
    );

    try {
      return await conversationService.submitTurnFeedback({
        sessionId,
        turnId,
        rating,
        comment,
      });
    } catch (error) {
      setMessages((prev) =>
        prev.map((message) =>
          message.turnId === turnId
            ? { ...message, feedback: previousFeedback }
            : message
        )
      );
      throw error;
    }
  };

  return (
    <KahuContext.Provider
      value={{
        messages,
        sendMessage,
        isSending,
        sendingSessionId,
        sessionId,
        setSessionId,
        setMessages,
        onCancelRequest,
        onResetSession,
        conversations,
        activeConversationId,
        currentView,
        unreadCounts,
        conversationsWithUnreadCounts,
        switchConversation,
        createNewConversation,
        goToConversationList,
        searchConversations: searchConversation,
        hasMultipleConversations,
        isLoadingConversations,
        isSwitchingConversation,
        submitFeedback,
        draftPrompt,
        setDraftPrompt,
      }}
    >
      {children}
    </KahuContext.Provider>
  );
};
